import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

import { useAuthStore } from '@/stores/auth.store'
import type { ApiResponse } from '@/types/api'

interface QueuedRequest {
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
  config: AxiosRequestConfig
}

let isRefreshing = false
let pendingQueue: QueuedRequest[] = []

const flushQueue = (error: unknown, token: string | null) => {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error)
      return
    }
    if (token && config.headers) {
      ;(config.headers as Record<string, string>).Authorization = `Bearer ${token}`
    }
    resolve(http.request(config))
  })
  pendingQueue = []
}

const http: AxiosInstance = axios.create({
  // Mismo host que el SPA (p.ej. bufete.midominio.com/api) → nginx hace proxy al
  // backend preservando el Host para la resolución de tenant. Sin CORS.
  baseURL: import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? '/api',
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const auth = useAuthStore()
  if (auth.accessToken) {
    config.headers.set('Authorization', `Bearer ${auth.accessToken}`)
  }
  return config
})

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const status = error.response?.status
    const originalConfig = error.config as AxiosRequestConfig & { _retry?: boolean }
    // El backend no distingue con cabecera el 401 por expiración, así que se
    // intenta el refresco ante CUALQUIER 401 (una sola vez por petición),
    // excepto en los propios endpoints de auth para no ciclar.
    const reqUrl = String(originalConfig?.url ?? '')
    const esEndpointAuth =
      reqUrl.includes('/auth/login') || reqUrl.includes('/auth/refresh') || reqUrl.includes('/auth/logout')

    // ---- 401: token expirado → refrescar y reintentar ----
    if (status === 401 && !esEndpointAuth && !originalConfig._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject, config: originalConfig })
        })
      }

      originalConfig._retry = true
      isRefreshing = true

      const auth = useAuthStore()
      try {
        const newToken = await auth.refresh()
        flushQueue(null, newToken)
        if (originalConfig.headers) {
          ;(originalConfig.headers as Record<string, string>).Authorization = `Bearer ${newToken}`
        }
        return http.request(originalConfig)
      } catch (refreshError) {
        flushQueue(refreshError, null)
        await auth.logout().catch(() => undefined)
        const { default: router } = await import('@/router')
        await router.push({ name: 'login' })
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // ---- 403: prohibido ----
    if (status === 403) {
      try {
        const { useToast } = await import('primevue/usetoast')
        const toast = useToast()
        toast.add({
          severity: 'warn',
          summary: 'Acceso denegado',
          detail: error.response?.data?.message ?? 'No tiene permisos para esta acción.',
          life: 5000,
        })
      } catch {
        // toast no disponible (SSR/contexto fuera de setup) — ignorar
      }
    }

    // ---- 422: validación ----
    if (status === 422) {
      const errors = error.response?.data?.errors
      return Promise.reject(Object.assign(error, { validationErrors: errors }))
    }

    // ---- 5xx: error servidor ----
    if (status && status >= 500) {
      try {
        const { useToast } = await import('primevue/usetoast')
        const toast = useToast()
        toast.add({
          severity: 'error',
          summary: 'Error del servidor',
          detail: error.response?.data?.message ?? 'Ocurrió un error inesperado.',
          life: 6000,
        })
      } catch {
        // ignorar
      }
    }

    return Promise.reject(error)
  },
)

export function useApi(): AxiosInstance {
  return http
}

export default http
